import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import api from "../lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function buildUrl(path) {
  if (!path) return "";
  if (path.startsWith("http")) return path;
  if (path.startsWith("/api/files/")) return `${BACKEND_URL}${path}`;
  return path;
}

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("it-IT", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

/**
 * `/giornale` — public magazine index.
 *
 * Editorial grid inspired by Apple News / Monocle. First article gets a
 * large-format hero card; the rest flow in a 3-column responsive grid.
 * Everything is served from `/api/articles` which only returns published
 * pieces.
 */
export default function Journal() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/articles")
      .then((r) => setArticles(Array.isArray(r.data) ? r.data : []))
      .catch(() => setArticles([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div
      className="min-h-screen pt-28 sm:pt-32 pb-24"
      style={{ background: "#FFFFFF" }}
      data-testid="journal-page"
    >
      <section className="px-6 sm:px-12 mb-14 sm:mb-20">
        <div className="max-w-6xl mx-auto">
          <motion.span
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-[11px] uppercase inline-block mb-4"
            style={{
              color: "#FF2800",
              letterSpacing: "0.3em",
              fontWeight: 500,
            }}
          >
            Giornale
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.05 }}
            className="text-3xl sm:text-5xl lg:text-6xl font-light text-balance"
            style={{ color: "#1D1D1F", letterSpacing: "-0.02em" }}
            data-testid="journal-headline"
          >
            Storie, prove, incontri.
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.9, delay: 0.2 }}
            className="mt-5 text-base sm:text-lg max-w-xl"
            style={{ color: "#6E6E73", lineHeight: 1.7 }}
          >
            Le auto che abbiamo provato, venduto, guidato. Come le abbiamo
            vissute — con parole nostre.
          </motion.p>
        </div>
      </section>

      {loading ? (
        <div
          className="py-24 text-center text-sm"
          style={{ color: "#6E6E73" }}
        >
          Caricamento…
        </div>
      ) : articles.length === 0 ? (
        <div
          className="py-24 text-center border border-dashed mx-6 sm:mx-12"
          style={{ borderColor: "rgba(0,0,0,0.08)", color: "#6E6E73" }}
          data-testid="journal-empty"
        >
          Nessun articolo pubblicato (per ora).
        </div>
      ) : (
        <section className="px-6 sm:px-12">
          <div
            className="max-w-3xl mx-auto"
            data-testid="journal-list"
          >
            {articles.map((a, idx) => (
              <ArticleRow
                key={a.id}
                article={a}
                isLast={idx === articles.length - 1}
              />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function ArticleRow({ article, isLast }) {
  const cover = buildUrl(article.cover_image_url);
  return (
    <Link
      to={`/giornale/${article.slug}`}
      className="group flex gap-4 sm:gap-6 py-6 sm:py-7"
      style={{
        borderBottom: isLast ? "none" : "1px solid rgba(0,0,0,0.08)",
      }}
      data-testid={`journal-card-${article.slug}`}
    >
      <div
        className="relative overflow-hidden flex-shrink-0"
        style={{
          width: 120,
          height: 90,
          background: "#F5F5F7",
          borderRadius: 8,
          border: "1px solid rgba(0,0,0,0.05)",
        }}
      >
        {cover ? (
          <img
            src={cover}
            alt={article.title}
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-[700ms] ease-out group-hover:scale-[1.05]"
          />
        ) : (
          <div
            className="absolute inset-0 flex items-center justify-center text-[9px]"
            style={{ color: "#6E6E73", letterSpacing: "0.28em" }}
          >
            GIORNALE
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <span
          className="text-[10px] uppercase block mb-1.5"
          style={{
            color: "#FF2800",
            letterSpacing: "0.28em",
            fontWeight: 500,
          }}
        >
          {fmtDate(article.published_at || article.created_at)}
          <span style={{ color: "#6E6E73", marginLeft: 8 }}>
            · 2 min di lettura
          </span>
          {article.author ? (
            <span style={{ color: "#6E6E73", marginLeft: 8 }}>
              · {article.author}
            </span>
          ) : null}
        </span>
        <h3
          className="text-base sm:text-lg font-normal leading-snug"
          style={{ color: "#1D1D1F", letterSpacing: "-0.01em" }}
        >
          {article.title}
        </h3>
        {(article.excerpt || article.subtitle) && (
          <p
            className="text-sm mt-1.5 line-clamp-2"
            style={{ color: "#6E6E73", lineHeight: 1.5 }}
          >
            {article.excerpt || article.subtitle}
          </p>
        )}
        <span
          className="inline-flex items-center gap-1 mt-3 text-[11px] uppercase transition-opacity group-hover:opacity-70"
          style={{
            color: "#FF2800",
            letterSpacing: "0.22em",
            fontWeight: 500,
          }}
          data-testid={`journal-read-more-${article.slug}`}
        >
          Leggi l&apos;articolo completo
          <span aria-hidden style={{ marginLeft: 2 }}>→</span>
        </span>
      </div>
    </Link>
  );
}
